import { useContext } from 'react';
import { Dialog, ModalDialog } from '@pelckmans/business-components/components/dialog';
import ModalContext from '../../../contexts/ModalContext';
import { useTranslation } from 'react-i18next';
import { WordlistMedialink } from '../_api/MedialinksApi';
import { Node } from '../_api/TableOfContentsApi';
import MedialinkContentType from '../_components/common/MedialinkContentType';
import { WordlistContextProvider } from './_contexts/WordlistContext';
import MaterialModalHeader from '../_modals/common/MaterialModalHeader';
import WordlistListMode from './WordlistListMode';
import WordlistViewModeContext, {
  WordlistViewMode,
  WordlistViewModeContextProvider,
} from './_contexts/WordlistViewModeContext';
import WordlistFocusMode from './WordlistFocusMode';
import classNames from 'classnames';
import { Module } from '../_api/ModuleApi';

import '../../../scss/ianua/components/wordlist.scss';

function WordlistHeader({ medialink, node, name }: { medialink?: WordlistMedialink; node?: Node; name?: string }) {
  if (!name) return null;

  if (medialink && node) {
    return (
      <MaterialModalHeader
        title={name}
        description={node.description}
        contentType={<MedialinkContentType medialink={medialink} />}
        tutorialId={medialink.contentType.faqTutorial}
      />
    );
  }

  return <Dialog.Header title={name} data-testid="dialog-header" />;
}

type Base = {
  id: string;
  wordlistId: string;
  module: Module;
};

type StandAloneWordlistProps = Base & {
  standalone: true;
  name?: never;
  node?: never;
  medialink?: never;
};

type WordlistAppProps = Base & {
  name: string;
  standalone?: never;
  node?: never;
  medialink?: never;
};

type WordlistProps = Base & {
  name: string;
  node: Node;
  medialink: WordlistMedialink;
  standalone?: never;
};

type Props = StandAloneWordlistProps | WordlistAppProps | WordlistProps;

export default function WordlistModal({ id, wordlistId, module, ...props }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);

  return (
    <ModalDialog
      id={id}
      onClose={() => closeModal(id)}
      closeTitle={t('ACTIONS.CLOSE')}
      className={classNames('material-dialog', 'wordlist', {
        'wordlist--standalone': props.standalone,
      })}
      fullScreen
      closeViaEscape={!props.medialink?.contentType.faqTutorial}
      closeOnClickOutside={!props.medialink?.contentType.faqTutorial}
    >
      <WordlistContextProvider wordlistId={wordlistId} module={module}>
        <WordlistViewModeContextProvider>
          <WordlistViewModeContext.Consumer>
            {({ viewMode }) => (
              <>
                <WordlistHeader name={props.name} medialink={props.medialink} node={props.node} />
                <Dialog.Body>
                  {viewMode === WordlistViewMode.LIST && <WordlistListMode />}
                  {viewMode === WordlistViewMode.FOCUS && <WordlistFocusMode />}
                </Dialog.Body>
              </>
            )}
          </WordlistViewModeContext.Consumer>
        </WordlistViewModeContextProvider>
      </WordlistContextProvider>
    </ModalDialog>
  );
}
